import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, formatRelative } from "../lib/api";
import AppShell from "../components/AppShell";
import { useAuth } from "../lib/auth";
import { Newspaper, ArrowRight, ArrowsClockwise, Globe, Tag, Star } from "@phosphor-icons/react";

export default function News() {
  const { user } = useAuth();
  const [posts, setPosts] = useState(null);
  const [corridors, setCorridors] = useState([]);
  const [active, setActive] = useState("all");
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    const params = {};
    if (active !== "all") params.corridor = active;
    if (search.trim()) params.q = search.trim();
    const { data } = await api.get("/news", { params });
    setPosts(data);
  };

  useEffect(() => {
    api.get("/news/corridors").then((r) => setCorridors(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [active]);

  const refresh = async () => {
    if (busy || !user || user.role !== "admin") return;
    setBusy(true);
    try {
      await api.post("/news/refresh");
      await load();
    } catch (_) {}
    finally { setBusy(false); }
  };

  const corridorMap = useMemo(() => {
    const m = {};
    for (const c of corridors) m[c.key] = c;
    return m;
  }, [corridors]);

  return (
    <AppShell>
      <div data-testid="news-page">
        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
            <div className="label-mono mb-2 flex items-center gap-2" style={{ color: "#E60012" }}>
              <Newspaper size={12} weight="fill" /> NEWS · DAILY MOBILITY INTEL
            </div>
            <h1 className="font-display text-white mb-3" style={{ fontSize: "clamp(36px, 5vw, 56px)", fontWeight: 900, letterSpacing: "-0.035em", lineHeight: 0.98 }}>
              Future mobility,<br /><span style={{ color: "#E60012" }}>summarized.</span>
            </h1>
            <p className="text-zinc-400 max-w-2xl">
              An AI-curated digest of breakthroughs across EV, autonomous corridors, urban
              micro-mobility, aerospace integration, sustainable infrastructure and AI logistics —
              with source attribution.
            </p>
          </div>
          {user && user.role === "admin" && (
            <button onClick={refresh} disabled={busy} className="btn btn-ghost btn-sm" data-testid="news-refresh-btn">
              {busy ? <span className="spinner mr-2" /> : <ArrowsClockwise size={12} weight="bold" />}
              {busy ? "Refreshing..." : "Refresh feeds"}
            </button>
          )}
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="news-corridor-chips">
          <button
            onClick={() => setActive("all")}
            className={`label-mono px-3 py-1.5 border ${active === "all" ? "border-white bg-[#1A1A1D] text-white" : "border-[#27272A] text-zinc-400 hover:text-white"}`}
            data-testid="news-chip-all"
          >ALL CORRIDORS</button>
          {corridors.map((c) => (
            <button
              key={c.key}
              onClick={() => setActive(c.key)}
              className={`label-mono px-3 py-1.5 border flex items-center gap-1.5 ${active === c.key ? "border-white bg-[#1A1A1D] text-white" : "border-[#27272A] text-zinc-400 hover:text-white"}`}
              style={active === c.key ? { borderColor: c.color, color: c.color } : {}}
              data-testid={`news-chip-${c.key}`}
            >
              <span className="hub-dot" style={{ color: c.color }} />
              {c.name.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Search */}
        <form
          onSubmit={(e) => { e.preventDefault(); load(); }}
          className="mb-8 max-w-md"
        >
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search news (e.g. 'solid-state', 'eVTOL', 'hydrogen')"
            className="input"
            data-testid="news-search"
          />
        </form>

        {posts === null ? (
          <div className="py-16 text-center label-mono"><span className="spinner mr-2 align-middle" />LOADING INTEL...</div>
        ) : posts.length === 0 ? (
          <div className="border border-dashed border-[#27272A] p-12 text-center" data-testid="news-empty">
            <Newspaper size={32} weight="light" className="mx-auto mb-3 text-zinc-600" />
            <div className="label-mono mb-2">NO ARTICLES YET</div>
            <p className="text-zinc-400 text-sm">The aggregator is fetching the latest feeds. Check back in a minute.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="news-grid">
            {posts.map((p) => {
              const isDigest = p.kind === "digest";
              const c = corridorMap[p.corridor] || { name: p.corridor, color: "#E60012" };
              const accent = isDigest ? "#E60012" : c.color;
              const label = isDigest ? "EDITORIAL DIGEST" : c.name.toUpperCase();
              return (
                <Link
                  key={p.id}
                  to={`/news/${p.slug}`}
                  className="card card-hover overflow-hidden flex flex-col"
                  data-testid={`news-card-${p.slug}`}
                  style={{ borderTop: `2px solid ${accent}` }}
                >
                  <div className="h-44 bg-[#0A0A0C] relative overflow-hidden">
                    {p.image_url ? (
                      <img src={p.image_url} alt="" className="w-full h-full object-cover" loading="lazy"
                           onError={(e) => { e.target.style.display = "none"; e.target.parentNode.classList.add("blueprint-grid-dense"); }} />
                    ) : (
                      <div className="w-full h-full blueprint-grid-dense flex flex-col items-center justify-center gap-2 text-center px-4">
                        <Newspaper size={28} weight="light" style={{ color: accent }} />
                        <span className="label-mono" style={{ color: accent }}>{label}</span>
                      </div>
                    )}
                    <div className="absolute top-2 left-2 label-mono px-2 py-1 bg-[#0A0A0C]/90 border flex items-center gap-1" style={{ color: accent, borderColor: accent }}>
                      {isDigest ? <Star size={9} weight="fill" /> : <span className="hub-dot mr-0.5" style={{ color: accent }} />}
                      {label}
                    </div>
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <div className="label-mono mb-2 flex items-center gap-1.5">
                      <Globe size={10} weight="bold" />
                      {p.source_name.toUpperCase()} · {formatRelative(p.published_at)}
                    </div>
                    <div className="font-display text-white mb-2 line-clamp-3" style={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.015em", lineHeight: 1.2 }}>
                      {p.title}
                    </div>
                    <p className="text-sm text-zinc-400 line-clamp-3 mb-3 flex-1">{p.summary}</p>
                    <div className="flex items-center justify-between pt-3 border-t border-dashed border-[#27272A] mt-auto">
                      {p.tags && p.tags.length > 0 ? (
                        <div className="flex items-center gap-1.5 overflow-hidden">
                          <Tag size={10} className="text-zinc-500" />
                          <span className="label-mono truncate">{p.tags.slice(0, 3).join(" · ")}</span>
                        </div>
                      ) : <span />}
                      <span className="label-mono flex items-center gap-1">READ <ArrowRight size={9} weight="bold" /></span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
